import React, { useMemo } from 'react';
import { defineMessages, IntlShape, MessageDescriptor, useIntl } from 'react-intl';

import { Checkbox, CheckboxGroup } from 'carbon-react/lib/components/checkbox';
import Message from 'carbon-react/lib/components/message';
import { RadioButton, RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Textarea from 'carbon-react/lib/components/textarea';
import Typography from 'carbon-react/lib/components/typography';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import {
  AnalysisFeedbackFieldErrors,
  AnalysisFeedbackRating,
  AnalysisFeedbackReasonCode,
  AnalysisFeedbackRecord
} from '../analysis-feedback-types';
import { useAnalysisFeedbackForm } from '../hooks/use-analysis-feedback-form';

const COMMENT_LIMIT = 2000;

const REASON_MESSAGES = defineMessages({
  accurate: { id: 'aiInsights.feedback.reason.accurate', defaultMessage: 'Insights felt accurate' },
  clear: { id: 'aiInsights.feedback.reason.clear', defaultMessage: 'Clear and easy to understand' },
  goodDetail: { id: 'aiInsights.feedback.reason.goodDetail', defaultMessage: 'Good level of detail' },
  hardToUnderstand: { id: 'aiInsights.feedback.reason.hardToUnderstand', defaultMessage: 'Hard to understand' },
  inaccurate: { id: 'aiInsights.feedback.reason.inaccurate', defaultMessage: 'Something looks inaccurate' },
  missedIssue: { id: 'aiInsights.feedback.reason.missedIssue', defaultMessage: 'Missed an important issue' },
  tooGeneric: { id: 'aiInsights.feedback.reason.tooGeneric', defaultMessage: 'Too vague or generic' },
  usefulRisks: { id: 'aiInsights.feedback.reason.usefulRisks', defaultMessage: 'Highlighted useful risks' }
});

const REASON_OPTIONS: Record<
  AnalysisFeedbackRating,
  { code: AnalysisFeedbackReasonCode; message: MessageDescriptor }[]
> = {
  up: [
    { code: 'clear', message: REASON_MESSAGES.clear },
    { code: 'accurate', message: REASON_MESSAGES.accurate },
    { code: 'useful_risks', message: REASON_MESSAGES.usefulRisks },
    { code: 'good_detail', message: REASON_MESSAGES.goodDetail }
  ],
  down: [
    { code: 'inaccurate', message: REASON_MESSAGES.inaccurate },
    { code: 'missed_issue', message: REASON_MESSAGES.missedIssue },
    { code: 'too_generic', message: REASON_MESSAGES.tooGeneric },
    { code: 'hard_to_understand', message: REASON_MESSAGES.hardToUnderstand }
  ]
};

const getReasonOptions = (rating: AnalysisFeedbackRating | null) => (rating ? REASON_OPTIONS[rating] : []);

const getOwnershipMessage = (feedback: AnalysisFeedbackRecord | null, isEditableByAuthor: boolean, intl: IntlShape) => {
  if (!feedback) {
    return null;
  }
  if (!feedback.is_active) {
    return intl.formatMessage({
      id: 'aiInsights.feedback.superseded',
      defaultMessage: 'Newer feedback has been submitted for this business analysis. This feedback is now read-only.'
    });
  }
  if (isEditableByAuthor) {
    return intl.formatMessage({
      id: 'aiInsights.feedback.activeAuthor',
      defaultMessage:
        'You submitted the latest feedback. You can update it until feedback is submitted for a newer analysis.'
    });
  }
  return intl.formatMessage({
    id: 'aiInsights.feedback.activeNonAuthor',
    defaultMessage:
      'Feedback has already been submitted. Only the person who submitted it can make changes. You can give feedback on a later analysis.'
  });
};

const getFooterMode = (feedback: AnalysisFeedbackRecord | null, isReadOnly: boolean) => {
  if (isReadOnly) {
    return FooterMode.CREATED;
  }
  return feedback ? FooterMode.UPDATE : FooterMode.CREATE;
};

const getGuidanceExcludedMessage = (feedback: AnalysisFeedbackRecord, intl: IntlShape) =>
  feedback.can_edit
    ? intl.formatMessage({
        id: 'aiInsights.feedback.guidanceExcludedEditable',
        defaultMessage:
          'We couldn’t apply the guidance created from this feedback, so it has been excluded from future analyses. Edit and save your feedback to have it evaluated again on the next scheduled analysis.'
      })
    : intl.formatMessage({
        id: 'aiInsights.feedback.guidanceExcludedReadOnly',
        defaultMessage:
          'We couldn’t apply the guidance created from this feedback, so it has been excluded from future analyses. Only the original author can edit the active feedback.'
      });

const getControlRefs = (
  rating: AnalysisFeedbackRating | null,
  setFirstControlRef: (element: HTMLElement | null) => void
) => ({
  down: rating === 'down' ? setFirstControlRef : undefined,
  up: rating === 'down' ? undefined : setFirstControlRef
});

const getScreenText = (
  feedback: AnalysisFeedbackRecord | null,
  fieldErrors: AnalysisFeedbackFieldErrors,
  intl: IntlShape
) => ({
  cancel: feedback
    ? intl.formatMessage({ id: 'aiInsights.feedback.cancelChanges', defaultMessage: 'Cancel changes' })
    : intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
  commentError: fieldErrors.comment?.length
    ? intl.formatMessage({
        id: 'aiInsights.feedback.commentTooLong',
        defaultMessage: 'Keep your comment to 2,000 characters or fewer.'
      })
    : undefined,
  ratingError: fieldErrors.rating?.length
    ? intl.formatMessage({ id: 'aiInsights.feedback.ratingRequired', defaultMessage: 'Choose Yes or No.' })
    : undefined,
  reasonsError: fieldErrors.reasons?.length
    ? intl.formatMessage({
        id: 'aiInsights.feedback.reasonsInvalid',
        defaultMessage: 'Choose reasons that match your rating.'
      })
    : undefined,
  submit: feedback
    ? intl.formatMessage({ id: 'aiInsights.feedback.saveChanges', defaultMessage: 'Save changes' })
    : intl.formatMessage({ id: 'aiInsights.feedback.sendFeedback', defaultMessage: 'Send feedback' })
});

const getOwnershipMessageVariant = (isReadOnly: boolean) => (isReadOnly ? 'info-subtle' : 'neutral');

const hasExcludedGuidance = (feedback: AnalysisFeedbackRecord | null) =>
  !!feedback?.is_active && feedback.guidance_application_status === 'excluded';

const hasPendingGuidance = (feedback: AnalysisFeedbackRecord | null) =>
  feedback?.guidance_application_status === 'pending';

interface AnalysisFeedbackFormScreenProps {
  feedback: AnalysisFeedbackRecord | null;
  form: ReturnType<typeof useAnalysisFeedbackForm>;
  onCancel: () => void;
}

export const AnalysisFeedbackFormScreen = ({ feedback, form, onCancel }: AnalysisFeedbackFormScreenProps) => {
  const intl = useIntl();
  const reasonOptions = useMemo(() => getReasonOptions(form.draft.rating), [form.draft.rating]);
  const ownershipMessage = getOwnershipMessage(feedback, form.isEditableByAuthor, intl);
  const footerMode = getFooterMode(feedback, form.isReadOnly);
  const controlRefs = getControlRefs(form.draft.rating, form.setFirstControlRef);
  const text = getScreenText(feedback, form.fieldErrors, intl);

  return (
    <WizardScreen
      cancelText={text.cancel}
      createDisabled={!form.draft.rating}
      createText={text.submit}
      fieldSpacing={3}
      footerMode={footerMode}
      onCancel={onCancel}
      onCreate={form.handleSubmit}
      onSubmit={event => event.preventDefault()}
      onUpdate={form.handleSubmit}
      updateDisabled={!form.draft.rating}
      updateText={text.submit}
    >
      <>
        {form.submitError && <WizardFormError message={form.submitError} mb={3} />}

        {form.fieldErrors.non_field_errors?.length && (
          <WizardFormError
            message={intl.formatMessage({
              id: 'aiInsights.feedback.validationError',
              defaultMessage: 'Check your feedback and try again.'
            })}
            mb={3}
          />
        )}

        <WizardRequiredFieldInfo />

        <Typography color="--colorsUtilityYin065" mb={1}>
          {intl.formatMessage({
            id: 'aiInsights.feedback.context',
            defaultMessage: 'Your feedback helps make future analysis more relevant to this business.'
          })}
        </Typography>

        {ownershipMessage && (
          <Message open variant={getOwnershipMessageVariant(form.isReadOnly)} mb={3}>
            {ownershipMessage}
          </Message>
        )}

        {hasExcludedGuidance(feedback) && feedback && (
          <Message
            open
            title={intl.formatMessage({
              id: 'aiInsights.feedback.guidanceExcludedTitle',
              defaultMessage: 'Guidance excluded'
            })}
            variant="warning"
          >
            {getGuidanceExcludedMessage(feedback, intl)}
          </Message>
        )}

        <RadioButtonGroup
          disabled={form.isReadOnly}
          error={text.ratingError}
          id="analysis-feedback-rating"
          inline
          legend={intl.formatMessage({
            id: 'aiInsights.feedback.usefulQuestion',
            defaultMessage: 'Was this analysis useful?'
          })}
          mb={3}
          name="analysis-feedback-rating"
          onChange={event => form.handleRatingChange(event.target.value as AnalysisFeedbackRating)}
          required
          value={form.draft.rating ?? ''}
        >
          <RadioButton
            id="analysis-feedback-rating-up"
            label={intl.formatMessage({ id: 'common.yes', defaultMessage: 'Yes' })}
            ref={controlRefs.up}
            value="up"
          />
          <RadioButton
            id="analysis-feedback-rating-down"
            label={intl.formatMessage({ id: 'common.no', defaultMessage: 'No' })}
            ref={controlRefs.down}
            value="down"
          />
        </RadioButtonGroup>

        {form.draft.rating && (
          <CheckboxGroup
            disabled={form.isReadOnly}
            error={text.reasonsError}
            id="analysis-feedback-reasons"
            legend={intl.formatMessage({
              id: 'aiInsights.feedback.reasonQuestion',
              defaultMessage: 'What shaped your rating?'
            })}
            legendHint={intl.formatMessage({
              id: 'aiInsights.feedback.reasonHelp',
              defaultMessage: 'Select all that apply'
            })}
            mb={3}
          >
            {reasonOptions.map(({ code, message }) => (
              <Checkbox
                checked={form.draft.reasons.includes(code)}
                disabled={form.isReadOnly}
                id={`analysis-feedback-reason-${code}`}
                key={code}
                label={intl.formatMessage(message)}
                onChange={() => form.handleReasonChange(code)}
                value={code}
              />
            ))}
          </CheckboxGroup>
        )}

        <Textarea
          characterLimit={COMMENT_LIMIT}
          error={text.commentError}
          id="analysis-feedback-comment"
          inputHint={intl.formatMessage({ id: 'aiInsights.feedback.commentHelp', defaultMessage: 'Optional' })}
          label={intl.formatMessage({
            id: 'aiInsights.feedback.commentLabel',
            defaultMessage: 'Anything else we should know?'
          })}
          maxLength={COMMENT_LIMIT}
          mb={3}
          onChange={event => form.handleCommentChange(event.target.value)}
          placeholder={intl.formatMessage({
            id: 'aiInsights.feedback.commentPlaceholder',
            defaultMessage: 'For example, tell us what felt inaccurate or what you want to see more of next time.'
          })}
          readOnly={form.isReadOnly}
          ref={form.commentRef}
          value={form.draft.comment}
        />

        <Message open variant="ai" mb={2}>
          {intl.formatMessage({
            id: 'aiInsights.feedback.guidanceDisclaimer',
            defaultMessage:
              'We’ll use this feedback as guidance when the analysis is next generated. It won’t change your source financial data.'
          })}
        </Message>

        {hasPendingGuidance(feedback) && (
          <Typography color="--colorsUtilityYin065" mb={0}>
            {intl.formatMessage({
              id: 'aiInsights.feedback.guidancePending',
              defaultMessage: 'This feedback will be evaluated when the next analysis is generated.'
            })}
          </Typography>
        )}
      </>
    </WizardScreen>
  );
};
